import { MoreOutlined } from '@ant-design/icons';
import { Button, Card, Dropdown, Menu, message, Modal } from 'antd';
import { Content } from 'antd/lib/layout/layout';
import * as React from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import AwardTypeApiService from '../../../../api/AwardTypeApiService';
import AuthorizedContent from '../../../../components/AuthorizedContent';
import AwardTypeDetailsForm from '../../../../components/forms/postSecondary/AwardTypeDetailsForm';
import EditCancelButton from '../../../../components/buttons/EditCancelButton';
import Breadcrumbs from '../../../../config/Breadcrumbs';
import Routes from '../../../../config/Routes';
import FeatureFlag from '../../../../consts/FeatureFlag';
import * as DeleteAwardTypeHandler from '../../../../handlerModels/DeleteAwardTypeHandler';
import * as GetAwardTypeDetailsHandler from '../../../../handlerModels/GetAwardTypeDetailsHandler';
import AwardTypeDTO from '../../../../models/AwardTypeDTO';
import HeaderPortal from '../../../../portals/HeaderPortal';
import BaseFormState from '../../../../redux/bases/BaseFormState';
import HistoryUtil from '../../../../utils/HistoryUtil';
import PageStayPrompt from '../../../../utils/PageStayPrompt';
import ParameterUtil from '../../../../utils/ParameterUtil';

interface AwardTypeDetailsPageState extends BaseFormState {
  awardTypeId: number;
  awardType?: AwardTypeDTO;
}

interface AwardTypeDetailsPageProps {
}

class AwardTypeDetailsPage extends React.Component<AwardTypeDetailsPageProps & RouteComponentProps<RouteObject>, AwardTypeDetailsPageState> {
  private readonly _detailsRef = React.createRef<AwardTypeDetailsForm>();

  constructor(props: AwardTypeDetailsPageProps & RouteComponentProps<RouteObject>) {
    super(props);

    this.state = {
      awardTypeId: 0
    };
  }

  componentDidMount() {
    const awardTypeId = ParameterUtil.getPathPart(this.props.match, 'id');

    if (awardTypeId == 'new') {
      this.setState({ isEditing: true, isNew: true, loading: false })
    }
    else {
      const awardTypeIdNumber = Number.parseInt(awardTypeId);
      this.loadAwardType(awardTypeIdNumber);
    }
  }

  private loadAwardType = (awardTypeId: number) => {
    this.setState({ awardTypeId: awardTypeId });

    AwardTypeApiService.getAwardTypeDetails(awardTypeId)
      .then((results: GetAwardTypeDetailsHandler.Result) => {
        if (results.awardType) {
          this.setState({ awardType: results.awardType });
        }
      }).catch(() => {
        this.setState({ error: true });
        message.error('Award Type could not be loaded')
      }).finally(() => {
        this.setState({ loading: false });
      });
  }

  private toggleEdit = () => {
    if (this.state.isEditing) {
      this._detailsRef.current?.resetForm();
    }

    this.setState({ isEditing: !this.state.isEditing });
  }

  private specialEmphasisDetailsSaved = (awardTypeId: number) => {
    this.setState({ isEditing: false });
    if (awardTypeId != this.state.awardTypeId) {
      HistoryUtil.replace(Routes.generate(Routes.AWARD_TYPE_DETAILS, { id: awardTypeId }));
    }
    else {
      this.loadAwardType(awardTypeId);
    }
  }

  private deleteConfirmation = (id: number) => {
    Modal.confirm({
      title: 'Are you sure you want to delete this Award Type?',
      okText: 'Delete',
      onOk: () => this.deleteAwardType(id)
    });
  }

  private deleteAwardType = (awardTypeId: number) => {
    this.setState({ submitting: true });

    const request = DeleteAwardTypeHandler.Request.create({
      awardTypeId: awardTypeId,
    });

    AwardTypeApiService.deleteAwardType(request)
      .then((result: DeleteAwardTypeHandler.Result | null) => {
        this.setState({
          error: !result?.succeeded,
          message: result?.errors.join('\n'),
          fieldErrors: result?.fieldErrors,
          altered: !result?.succeeded
        });

        if (result?.succeeded === true) {
          message.success('Award Type was removed.');
        }
        else {
          message.error(result?.errors.join('\n'));
        }

      })
      .catch((results: any) => {
        this.setState({ error: results });
        message.error('Award Type could not be removed.');
      })
      .finally(() => {
        this.setState({ loading: false });
        this.goToAwardType();
      });
  }

  private goToAwardType = () => {
    HistoryUtil.goBack();
  }

  render() {
    const title = this.state.loading ? '...' : this.state.awardTypeId == 0 ? 'New Award Type' : this.state.awardType?.name ?? '...';

    return (
      <Content >
        <HeaderPortal
          title={title}
          onBack={this.goToAwardType}
          breadcrumbs={Breadcrumbs.awardTypeDetails(title, this.state.awardTypeId)}
          extra={this.renderMoreActions()} />
        <PageStayPrompt when={this.state.isEditing} />

        {this.renderDetailsCard()}
      </Content>
    );
  }

  renderDetailsCard() {
    const editButton = this.state.awardTypeId == 0 ?
      null :
      (
        <AuthorizedContent validFeatureFlags={[FeatureFlag.EDIT_AWARD_TYPE]}>
          <EditCancelButton isEditing={this.state.isEditing} onClick={this.toggleEdit} />
        </AuthorizedContent>
      );

    return (
      <Card title="Details" extra={editButton}>
        <AwardTypeDetailsForm
          ref={this._detailsRef}
          awardTypeId={this.state.awardTypeId}
          isEditing={this.state.isEditing}
          isNew={this.state.isNew}
          onSave={this.specialEmphasisDetailsSaved} />
      </Card>
    );
  }

  renderMoreActions() {
    const menu = (
      <Menu>
        < Menu.Item title='Delete Award Type' key={this.state.awardTypeId} onClick={() => this.deleteConfirmation(this.state.awardTypeId)}  >
          Delete Award Type
        </Menu.Item>
      </Menu>
    );
    return (
      <AuthorizedContent validFeatureFlags={[FeatureFlag.EDIT_AWARD_TYPE]}>
        <Dropdown overlay={menu}>
          <Button size='large' type='link' >
            <MoreOutlined />
          </Button>
        </Dropdown>
      </AuthorizedContent>
    );
  }
}

export default withRouter(AwardTypeDetailsPage);
